import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { ContactsSelector } from "../../utils/moduleSelectors/contactSelector";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { DepartmentSelector } from "../../utils/moduleSelectors/departmentSelector";
import { PromangRichTextEditor } from "../../utils/PromangRichTextEditor";
import { updateOfferAsync, getOfferAsync } from "../../../slices/offer";
import { mapErrors } from "../../Utils";
import {
  DOCUMENT_BLOCK_TYPES,
  getDocumentBlockBodyByType,
  normalizeDocumentStructure,
  serializeDocumentStructure,
  upsertOfferEditSourcedBodies,
} from "./offer-document-structure-defaults";
import { resolveOfferLayout } from "./utils/offer-layout";
import "./offer-workspace.css";

/**
 * Currencies listed in the dropdown. Only NOK is selectable for now —
 * others are shown disabled so we can enable multi-currency later without
 * changing the UI shape.
 */
const CURRENCY_OPTIONS = [
  { code: "NOK", enabled: true },
  { code: "SEK", enabled: false }, // future
  { code: "DKK", enabled: false }, // future
  { code: "EUR", enabled: false }, // future
  { code: "USD", enabled: false }, // future
];

const TABS = ["stamdata", "comments", "project_description"];

/**
 * Stamdata / Kommentarer / Prosjektbeskrivelse edit dialog.
 * Rich text fields sync to offer.comment + document_structure blocks
 * (Prosjektbeskrivelse / Omfang are not editable in the document builder).
 *
 * Scroll: avoided when content fits; overflow-y-auto on the tab body when
 * it exceeds the dialog max-height.
 */
export default function OfferEditDialog({ open, onClose, offer, onSaved }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [tab, setTab] = useState("stamdata");
  const [selectedDepartment, setSelectedDepartment] = useState(
    offer?.department?.id ? offer?.department : null
  );

  useEffect(() => {
    if (!open) return;
    setTab("stamdata");
    setSelectedDepartment(offer?.department?.id ? offer?.department : null);
  }, [open, offer?.id, offer?.department]);

  const handleProjectChange = (data, formik) => {
    const { setFieldValue, values } = formik;
    if (data?.department?.id && !values.department_id) {
      setFieldValue("department_id", data?.department?.id);
      setSelectedDepartment(data?.department);
    }
  };

  const documentStructure = normalizeDocumentStructure(
    offer?.document_structure,
    t,
    offer
  );
  const initialProjectDescription = getDocumentBlockBodyByType(
    documentStructure,
    DOCUMENT_BLOCK_TYPES.PROJECT_DESCRIPTION
  );
  const initialScope = getDocumentBlockBodyByType(
    documentStructure,
    DOCUMENT_BLOCK_TYPES.SCOPE
  );

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("offers.edit.title")}
      size="4xl"
      closeOnOutsideClick
      // Child form pins Lagre/Avbryt; only the tab body scrolls.
      contentClassName="!overflow-hidden"
      panelClassName="sm:!max-h-[min(85vh,880px)]"
    >
      <Formik
        enableReinitialize
        initialValues={{
          id: offer?.id,
          customer_id: offer?.customer?.id,
          contact_id: offer?.contact?.id,
          project_id: offer?.project?.id,
          department_id: offer?.department?.id,
          our_contact_employee_id:
            offer?.our_contact_employee?.id || currentUser?.id,
          title: offer?.title || "",
          reference: offer?.reference || "",
          offer_number: offer?.offer_number || "",
          offer_date: offer?.offer_date || "",
          valid_from: offer?.valid_from || "",
          valid_to: offer?.valid_to || "",
          currency: offer?.currency || "NOK",
          comment: offer?.comment || "",
          project_description: initialProjectDescription,
          scope: initialScope,
        }}
        validationSchema={Yup.object({
          title: Yup.string().max(255),
          customer_id: Yup.string().required(t("offers.edit.customer_required")),
        })}
        onSubmit={async (values, helpers) => {
          try {
            helpers.setSubmitting(true);

            const payload = {
              id: values.id,
              customer_id: values.customer_id,
              contact_id: values.contact_id,
              project_id: values.project_id,
              department_id: values.department_id,
              our_contact_employee_id: values.our_contact_employee_id,
              title: values.title,
              reference: values.reference,
              offer_number: values.offer_number,
              offer_date: values.offer_date,
              valid_from: values.valid_from,
              valid_to: values.valid_to,
              currency: values.currency,
              comment: values.comment || "",
            };

            const structureDirty =
              values.project_description !== initialProjectDescription ||
              values.scope !== initialScope;
            const isDocumentLayout = resolveOfferLayout(offer) === "document";

            // Always sync comment. Persist document_structure when the offer is
            // already on document layout, or when Prosjektbeskrivelse/Omfang changed
            // (avoids flipping simple → document on stamdata-only saves).
            if (isDocumentLayout || structureDirty) {
              const nextStructure = upsertOfferEditSourcedBodies(
                offer?.document_structure,
                {
                  projectDescription: values.project_description || "",
                  scope: values.scope || "",
                },
                t,
                offer
              );
              payload.document_structure = serializeDocumentStructure(nextStructure);
            }

            const result = await dispatch(updateOfferAsync(payload));

            if (updateOfferAsync.rejected.match(result)) {
              const resultPayload = result.payload;
              const message =
                mapErrors(
                  resultPayload?.response?.data?.errors || resultPayload?.errors
                ) ||
                resultPayload?.response?.data?.error ||
                resultPayload?.message ||
                t("Noe gikk galt. Kunne ikke lagre data!");
              toast.error(message);
              helpers.setErrors({ submit: message });
              return;
            }

            toast.success(t("Data ble lagret!"));
            if (values.id) {
              await dispatch(getOfferAsync(values.id));
            }
            onSaved?.();
            onClose?.();
          } catch (err) {
            console.error("Error saving offer:", err);
            toast.error(t("Noe gikk galt. Kunne ikke lagre data!"));
            helpers.setErrors({ submit: err.message });
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form className="flex h-full min-h-0 flex-1 flex-col gap-4">
            <div
              className="inline-flex w-fit shrink-0 rounded-[7px] border border-slate-200 bg-slate-50 p-0.5"
              role="tablist"
            >
              {TABS.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={tab === id}
                  onClick={() => setTab(id)}
                  className={`rounded-[6px] px-3 py-1.5 text-xs font-semibold ${
                    tab === id
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t(`offers.edit.tab.${id}`)}
                </button>
              ))}
            </div>

            {/* Scroll only the tab body — footer stays pinned below */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-0.5">
              {tab === "stamdata" ? (
                <div className="grid grid-cols-6 gap-5 pb-1">
                  <div className="col-span-6 sm:col-span-3">
                    <CustomerSelector
                      label={t("offers.edit.customer")}
                      selected={offer?.customer?.id ? offer?.customer : null}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <ContactsSelector
                      label={t("offers.edit.contact")}
                      name="contact_id"
                      module_param={offer?.customer?.id}
                      selected={offer?.contact?.id ? offer?.contact : null}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <ProjectSelector
                      label={t("offers.edit.project")}
                      selected={offer?.project?.id ? offer?.project : null}
                      onChange={(data) => handleProjectChange(data, formik)}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <DepartmentSelector
                      label={t("offers.edit.department")}
                      selected={selectedDepartment}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <EmployeeSelector
                      name="our_contact_employee_id"
                      label={t("offers.edit.responsible")}
                      selected={
                        offer?.our_contact_employee?.id
                          ? offer?.our_contact_employee
                          : currentUser
                      }
                      {...formik}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <label
                      htmlFor="offer-currency"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("offers.edit.currency")}
                    </label>
                    <select
                      id="offer-currency"
                      name="currency"
                      value={formik.values.currency || "NOK"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="offer-select mt-2"
                    >
                      {CURRENCY_OPTIONS.map((opt) => (
                        <option
                          key={opt.code}
                          value={opt.code}
                          disabled={!opt.enabled}
                        >
                          {t(`offers.edit.currency_option.${opt.code}`, {
                            defaultValue: opt.code,
                          })}
                          {!opt.enabled
                            ? ` (${t("offers.edit.currency_coming_soon")})`
                            : ""}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                      {t("offers.edit.currency_hint")}
                    </p>
                  </div>

                  <div className="col-span-6 sm:col-span-4">
                    <PMField
                      name="title"
                      type="text"
                      label={t("offers.edit.offer_title")}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <PMField
                      name="offer_number"
                      type="text"
                      label={t("offers.edit.offer_number")}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-6">
                    <PMField
                      name="reference"
                      type="text"
                      label={t("offers.edit.reference")}
                      {...formik}
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-2">
                    <PMField
                      name="offer_date"
                      type="date"
                      label={t("offers.edit.offer_date")}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <PMField
                      name="valid_from"
                      type="date"
                      label={t("offers.edit.valid_from")}
                      {...formik}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <PMField
                      name="valid_to"
                      type="date"
                      label={t("offers.edit.valid_to")}
                      {...formik}
                    />
                  </div>
                </div>
              ) : null}

              {tab === "comments" ? (
                <div className="flex flex-col gap-4 pb-1">
                  <RichTextField
                    label={t("offers.edit.comment")}
                    hint={t("offers.edit.comment_hint")}
                    value={formik.values.comment}
                    onChange={(html) => formik.setFieldValue("comment", html)}
                    placeholder={t("offers.edit.comment_placeholder")}
                    editorSize="md"
                  />
                </div>
              ) : null}

              {tab === "project_description" ? (
                <div className="flex flex-col gap-5 pb-1">
                  <RichTextField
                    label={t("offers.edit.project_description")}
                    hint={t("offers.edit.project_description_hint")}
                    value={formik.values.project_description}
                    onChange={(html) =>
                      formik.setFieldValue("project_description", html)
                    }
                    placeholder={t(
                      "offers.edit.project_description_placeholder"
                    )}
                    editorSize="md"
                  />
                  <RichTextField
                    label={t("offers.edit.scope")}
                    hint={t("offers.edit.scope_hint")}
                    value={formik.values.scope}
                    onChange={(html) => formik.setFieldValue("scope", html)}
                    placeholder={t("offers.edit.scope_placeholder")}
                    editorSize="md"
                  />
                </div>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 bg-white pt-4">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
              >
                {t("offers.edit.cancel")}
              </button>
              <button
                type="submit"
                disabled={formik.isSubmitting}
                className="inline-flex items-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {formik.isSubmitting
                  ? t("offers.edit.saving")
                  : t("offers.edit.save")}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
}

function RichTextField({
  label,
  hint,
  value,
  onChange,
  placeholder,
  editorSize = "md",
}) {
  return (
    <div className="min-w-0">
      <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      {hint ? <p className="mb-1.5 text-xs text-slate-500">{hint}</p> : null}
      <PromangRichTextEditor
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        size={editorSize}
        framed
      />
    </div>
  );
}
